import { Check } from 'lucide-react';
import type { ReactNode } from 'react';
import { css, cx } from 'styled-system/css';

export function planColumnBorderLeft(): string {
  return css({
    borderLeft: {
      base: 'none',
      md: '1px solid token(colors.border.hairline)',
    },
  });
}

export interface PlanCopy {
  name: string;
  description: string;
  price: string;
  priceDetail: string;
  ctaNote: string;
  includesLabel: string;
  points: string[];
}

export function PlanColumn({
  plan,
  featured,
  cta,
  control,
}: {
  plan: PlanCopy;
  featured: boolean;
  cta: ReactNode;
  control?: ReactNode;
}) {
  return (
    <article
      data-testid={`pricing-${plan.name.toLowerCase()}`}
      data-featured={featured || undefined}
      className={cx(
        planColumnBorderLeft(),
        css({
          position: 'relative',
          display: { base: 'flex', md: 'grid' },
          flexDirection: 'column',
          gridRow: { md: 'span 5' },
          gridTemplateRows: { md: 'subgrid' },
          rowGap: '0',
          padding: { base: '6', md: '8' },
          borderTop: {
            base: '1px solid token(colors.border.hairline)',
            md: 'none',
          },
          _first: { borderTop: 'none', borderLeft: 'none' },
          '&[data-featured]::before': {
            content: '""',
            position: 'absolute',
            top: '-1px',
            left: '-1px',
            right: '-1px',
            height: '2px',
            background: 'var(--prism-ring)',
          },
        }),
      )}
    >
      <div>
        <div
          className={css({
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '3',
            minHeight: '8',
          })}
        >
          <h2
            className={css({
              fontFamily: 'sans',
              fontSize: 'sm',
              fontWeight: 'semibold',
              lineHeight: '24px',
              margin: '0',
            })}
          >
            {plan.name}
          </h2>
          {control}
        </div>
        <p
          className={css({
            marginTop: '1.5',
            fontSize: 'sm',
            lineHeight: 'normal',
            color: 'text.secondary',
            textWrap: 'pretty',
          })}
        >
          {plan.description}
        </p>
      </div>
      <div className={css({ marginTop: { base: '6', md: '8' } })}>
        <p
          className={css({
            fontFamily: 'display',
            fontWeight: 'bold',
            fontSize: '2xl',
            letterSpacing: 'tight',
            lineHeight: 'tight',
            fontVariantNumeric: 'tabular-nums',
          })}
        >
          {plan.price}
        </p>
        <p
          className={css({
            marginTop: '1.5',
            fontSize: 'xs',
            color: 'text.secondary',
          })}
        >
          {plan.priceDetail}
        </p>
      </div>
      <div
        className={css({
          marginTop: '6',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'stretch',
          gap: '2.5',
        })}
      >
        {cta}
        <p className={css({ fontSize: 'xs', color: 'text.secondary' })}>
          {plan.ctaNote}
        </p>
      </div>
      <p
        className={css({
          marginTop: '7',
          paddingTop: '6',
          borderTop: '1px solid token(colors.border.hairline)',
          textStyle: 'eyebrow',
          color: 'text.muted',
        })}
      >
        {plan.includesLabel}
      </p>
      <ul
        className={css({
          listStyle: 'none',
          padding: 0,
          marginTop: '4',
          display: 'flex',
          flexDirection: 'column',
          gap: '3',
          fontSize: 'sm',
          lineHeight: 'normal',
          color: 'text.primary',
        })}
      >
        {plan.points.map((point) => (
          <li
            key={point}
            className={css({
              display: 'grid',
              gridTemplateColumns: '16px 1fr',
              columnGap: '3',
              alignItems: 'start',
            })}
          >
            <Check
              aria-hidden="true"
              className={css({
                width: '4',
                height: '4',
                marginTop: '0.5',
                color: 'text.muted',
              })}
            />
            {point}
          </li>
        ))}
      </ul>
    </article>
  );
}
